import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Phone, Clock } from 'lucide-react';
import { getContacts } from '../services/api';
import { timeAgo, pipelineLabel, pipelineBadge } from '../utils';

const STAGES = [
  { key: '', label: 'All' },
  { key: 'new_lead', label: 'New Leads' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'proposal_sent', label: 'Proposal' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
];

export default function ContactsList() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    getContacts(stage || null, search || null)
      .then(res => setContacts(res.data.contacts || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [stage, search]);

  return (
    <div className="page-wrap">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Contacts</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
          {contacts.length} contacts in your CRM
        </p>
      </div>

      {/* Search + Filter */}
      <div style={{ marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div className="search-bar-wrap">
          <Search size={14} color="var(--text-muted)" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search contacts..."
            style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: 13, flex: 1 }}
          />
        </div>

        <div className="filter-bar">
          {STAGES.map(s => (
            <button
              key={s.key}
              onClick={() => setStage(s.key)}
              className={stage === s.key ? 'btn btn-primary' : 'btn btn-ghost'}
              style={{ fontSize: 12, padding: '6px 12px' }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : contacts.length === 0 ? (
          <div className="empty-state">
            <Users size={32} />
            <p>No contacts yet</p>
            <p style={{ fontSize: 12 }}>Contacts are created automatically from inbound calls</p>
          </div>
        ) : (
          <div className="table-wrap">
          <table className="resp-table">
            <thead>
              <tr>
                <th>Contact</th>
                <th>Phone</th>
                <th>Pipeline Stage</th>
                <th className="desktop-only">Total Calls</th>
                <th>Follow-up</th>
                <th className="desktop-only">Last Called</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(contact => (
                <tr key={contact.id} onClick={() => navigate(`/contacts/${contact.id}`)}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <div style={{
                        width: 32, height: 32,
                        background: 'var(--bg-hover)',
                        borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 600, color: 'var(--blue-light)',
                        flexShrink: 0,
                      }}>
                        {(contact.name || contact.phone || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.name || '—'}</div>
                        {contact.company && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.company}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {contact.phone}
                  </td>
                  <td>
                    <span className={`badge ${pipelineBadge(contact.pipeline_stage)}`}>
                      {pipelineLabel(contact.pipeline_stage)}
                    </span>
                  </td>
                  <td className="desktop-only" style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Phone size={12} color="var(--text-muted)" />
                      {contact.total_calls || 0}
                    </div>
                  </td>
                  <td>
                    {contact.follow_up_date ? (
                      <div style={{
                        fontSize: 12,
                        color: new Date(contact.follow_up_date) < new Date() ? 'var(--red)' : 'var(--orange)',
                        display: 'flex', alignItems: 'center', gap: 4,
                        whiteSpace: 'nowrap',
                      }}>
                        <Clock size={11} />
                        {timeAgo(contact.follow_up_date)}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td className="desktop-only" style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {timeAgo(contact.last_called_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
